import { ol_Feature } from "@omnia/exports/openlayers"
import { formatPct } from "../../formatters"
import { color, soilColors, convertAnalysisAttributeToUnit, convertMorganToOlsen, convertOlsenToMorgan, getMorganAttribute, getMorganIndexTextFromValue, isNumber, isObject, ISoilAnalysisColor, isString, MorganAttribute, soil, SoilUnit, NumericValuesViewer, NumericValueColorProvider, INumericValuesViewerOptions, createValuedColorsProvider, IDatalayerMapInstance, IDatalayerViewerFactory, INumericValueColor, groupBy, IDatalayerKey, IDatalayerKeyItem, ZoneTextLocation, LayerEditMode } from "../../omnia"
import { ssmClassColors } from "../../ssm/colors"
import { SsmAttribute } from "../../ssm/types"
import { IDataLayerType, ISoilAnalysisLayerViewerFactory, LayerTypeId, LayerZone, NitrogenAnalysisType } from "../../types"
import { SoilAnalysisZone } from "../soilAnalysisZone"

export interface ISoilAnalysisViewerOptions {

    name?: string
    colors?: ISoilAnalysisColor[]
    formatter?: (value: any) => string

}

export class SoilAnalysisViewer extends NumericValuesViewer<SoilAnalysisZone> {

    public static getFactory(elementProperty: string, units: string | (() => SoilUnit), options?: ISoilAnalysisViewerOptions): IDatalayerViewerFactory {

        options = options || {}
        const { name, colors } = options

        let colorProvider: NumericValueColorProvider

        if (colors) {
            colorProvider = createValuedColorsProvider(colors)
        }

        const genericOptions: INumericValuesViewerOptions = {
            dataProperty: elementProperty,
            units: isString(units) ? units : null,
            colorProvider
        }

        const factory: ISoilAnalysisLayerViewerFactory = {

            typeId: LayerTypeId.SoilAnalysis,
            name: name || elementProperty,
            key: elementProperty,

            createViewer: (layer: IDatalayerMapInstance<SoilAnalysisZone>) => new SoilAnalysisViewer(layer, units, genericOptions, options),

            appliesToType: (type: IDataLayerType) => checkIfViewerAppliesToType(elementProperty, type),

            colors,
            units,

        }

        return factory
    }

    private _keyItems: {

        value: any
        text: string
        color: any

        minValue: number
        maxValue: number

    }[]


    private unitsProvider: () => SoilUnit

    /** Set if the current data property matches a Morgan property */
    private morgan = getMorganAttribute(this._dataProperty)

    /** True if using a Morgan color key */
    private morganKey: boolean

    /** SSM attribute defining the property, if applicable */
    private ssmAttribute: SsmAttribute



    constructor(layer: IDatalayerMapInstance<SoilAnalysisZone>, units: string | (() => SoilUnit), genericOptions: INumericValuesViewerOptions, private localOptions: ISoilAnalysisViewerOptions) {

        super(layer, genericOptions)

        const options = this.localOptions = this.localOptions ?? {}
        const { colors } = options

        if (units instanceof Function) {
            this.unitsProvider = units
        } else if (!!units) {
            this._units = units.toString()
        }

        if (this.morgan && checkMorganForAllZones(layer)) {
            this._keyItems = soil.colors.getMorganKey(this.morgan)
            this.morganKey = true
        }
        else if (colors) {
            this.setupKeyFromColors(colors)
        }

    }

    private setupKeyFromColors(colors: INumericValueColor[]) {

        const items = colors.map(c => {
            return { key: color.colorToCss(c.Colour), value: c.Value, item: c }
        })

        const groups = groupBy(items, i => i.key)

        let keyItems = groups.map(g => {

            var values = g.values.map(i => i.value)

            var minValue = Math.min.apply(Math, values)

            return {

                value: minValue,
                text: "",
                color: g.key,

                minValue: minValue,
                maxValue: 0,

            }

        })

        keyItems = keyItems.sort((i1, i2) => i1.minValue - i2.minValue)

        const lastIndex = keyItems.length - 1

        keyItems.forEach((item, index) => {

            if (index < lastIndex) {
                item.maxValue = keyItems[index + 1].value
                item.text = this.getValueTextInternal(item.minValue, false) + " - " + this.getValueTextInternal(item.maxValue, true)
            }
            else {
                item.maxValue = Number.POSITIVE_INFINITY
                item.text = "> " + this.getValueTextInternal(item.minValue, true)
            }

        })

        this._keyItems = keyItems

    }

    getFeatureFillColor(feature: ol_Feature): any {


        const { _layer: layer, morgan, unitsProvider } = this

        /* Specific implementation if the layer is being edited */

        const editMode = layer.editMode

        if (!!editMode) {
            return getEditModeFillColor(feature, layer)
        }

        const zone = getSoilZoneFromFeature(feature)

        if (zone) {

            /* Specific implementation for SSM analysis with a classification */

            const ssm = this.getSsmInfoFromZone(zone)

            if (useSsmClassification(ssm)) {
                return getSsmFillColor(zone, ssm)
            }

            /* Specific implementation for Morgan analysis */

            if (morgan) {

                // If true, the zone is linked to a Morgan analysis
                const morganZone = checkMorganForZone(zone)

                const unit = unitsProvider ? unitsProvider() : unitsProvider

                if (morganZone && (unit !== SoilUnit.Ppm)) {
                    return getMorganFillColor(zone, morgan)
                }

                if (morganZone && (unit === SoilUnit.Ppm)) {
                    let value = this.getZoneValue(zone)
                    value = convertMorganToOlsen(morgan, value)
                    return this.getValueFillColor(value)
                }

                if ((!morganZone) && (unit === SoilUnit.Morgan)) {
                    let value = this.getZoneValue(zone)
                    value = convertOlsenToMorgan(morgan, value)
                    return soilColors.getMorganColor(morgan, value)
                }

            }

        }

        /* For standard analysis, use the default implementation */

        return super.getFeatureFillColor(feature)

    }

    getKey(): IDatalayerKey {

        let { _keyItems: items } = this

        if (items) {

            const zones = this.filterVisibleZones(this._layer.zones)
            const values = zones.map(z => this.getZoneValue(z)).unique(v => v)
            const last = items.length - 1

            const layerKey = items
                .filter((i, index) => values.some(v =>
                    ((v >= i.minValue) && (v < i.maxValue))
                    || ((index == 0) && (v < i.minValue))
                    || ((index == last) && (v >= i.maxValue))
                ))

            return this.setupKey({ typeKey: items, layerKey, fixed: true })

        }

        return this.setupKey(super.getKey())

    }

    protected setupKey(key: { typeKey: IDatalayerKeyItem[], layerKey: IDatalayerKeyItem[], fixed: boolean }) {

        const [sid, sname] = this.morganKey
            ? ["-morgan", " (Morgan)"]
            : ["", ""]

        const id = `soil-analysis-${this._dataProperty}${sid}`
        const name = "Soil analysis - " + this.localOptions.name + sname

        return {
            ...key,
            id,
            name,
            numeric: true,
            unit: this._units
        }
    }

    getZoneText(zone: LayerZone, location: ZoneTextLocation): string {

        const soilAnalysisZone = <SoilAnalysisZone>zone
        const soilAnalysis = soilAnalysisZone.getSoilAnalysis()

        const editMode = this._layer.editMode

        if (!!editMode) {

            if (soilAnalysis) return soilAnalysis.AnalysisNumber
            return (editMode == LayerEditMode.EditTarget ? "Unallocated" : "")

        }

        let text = ""

        if (location != ZoneTextLocation.MapTooltip) {

            if (zone instanceof SoilAnalysisZone) {
                text = zone.zoneName
            }
            else if (zone.zone.Name) {
                text = zone.zone.Name
            }
            else if (zone.zone.Number) {
                text = "Zone " + zone.zone.Number.toString()
            }

        }

        text += "\n" + super.getZoneText(zone, location)

        if (soilAnalysis) {

            if ((location == ZoneTextLocation.MapFeature) && editMode) {
                text += "\n" + soilAnalysis.AnalysisNumber
            }

        }

        return text

    }

    getValueText(value: number, location?: ZoneTextLocation, zone?: LayerZone): string {

        if (isNaN(value)) {
            return (location == ZoneTextLocation.MapFeature) ? "" : "(No value)"
        }

        const { morgan, unitsProvider } = this

        if (morgan && unitsProvider) {

            // Convert the value to Morgan/Olsen if required

            const soilZone = <SoilAnalysisZone>zone
            const units = unitsProvider()

            value = convertAnalysisAttributeToUnit(soilZone.getSoilAnalysis(), morgan, units)

        }

        return this.getValueTextInternal(value, (location != ZoneTextLocation.MapFeature), zone)

    }

    private getValueTextInternal(value: number, includeUnits: boolean, zone?: LayerZone): string {

        let text: string
        let unitsText = this._units

        const { unitsProvider, morgan, _dataProperty: prop, localOptions } = this

        const soilZone = <SoilAnalysisZone>zone

        if (unitsProvider) {

            const units = unitsProvider()

            if (units == SoilUnit.Index) {

                if (morgan && checkMorganForZone(soilZone)) {
                    return getMorganIndexTextFromValue(this.morgan, value)
                }

                const index = soilColors.getValueIndex(value, prop)
                return (isNumber(index) ? index.toString() : "")


            }
            else if (zone && (units == SoilUnit.KgHa)) {

                const analysis = soilZone.getAnalysis()
                const type = <NitrogenAnalysisType>analysis["AnalysisType"]

                if (type == NitrogenAnalysisType.FarmAssessment) {
                    return ""
                }

            }

            unitsText = soil.getSoilUnitText(units)

        }

        if (localOptions.formatter) {
            text = localOptions.formatter(value)
            includeUnits = false
        }
        else if (unitsText == "%") {
            text = formatPct(value, 0)
            includeUnits = false
        } else {
            text = value.toString()
        }

        if (unitsText && includeUnits) {
            text += " " + unitsText
        }

        return text

    }

    private getSsmInfoFromZone(zone: SoilAnalysisZone) {

        if (zone.isSsmAnalysis()) {
            this.ssmAttribute = this.ssmAttribute ?? zone.getSsmAttribute(this._dataProperty)
            return this.ssmAttribute
        }

    }



}

function checkIfViewerAppliesToType(element: string, type: IDataLayerType): boolean {

    if (type && (type.TypeId == LayerTypeId.SoilAnalysis)) {

        var analysis = type.Values.map(v => v.Data)

        return analysis.some(a => isNumber(a[element]))

    }

}

function getEditModeFillColor(feature: ol_Feature, layer: IDatalayerMapInstance) {

    const opacity = (layer.editMode == LayerEditMode.EditTarget ? 1 : 0.5)

    const zone = <SoilAnalysisZone>feature.get("data")
    const sa = zone.getSoilAnalysis()

    const res = (sa ? [0x06, 0x79, 0xca, opacity] : [255, 255, 255, opacity])

    return res

}

/**
 * Returns the soil analysis zone linked to a feature
 */
function getSoilZoneFromFeature(feature: ol_Feature): SoilAnalysisZone {

    const zone = feature.get("data")

    return (zone instanceof SoilAnalysisZone) ? zone : undefined

}

/**
 * Indicates if the SSM classification/colours should be used for the SSM attribute specified
 * This will be the case if it:
 * - is set
 * - does not have a standard gauge colour key (which is then used - this is the case for the basic P, K, M, and pH properties)
 * - and has an SSM classification function - i.e., a function mapping a value to an SSM class
 */
function useSsmClassification(ssm: SsmAttribute) {
    return ssm && (!ssm.gaugeColorKey) && ssm.hasClassification
}

/**
 * Returns the fill colour to use for the value of an SSM attribute, as specified through the SSM classification
 * @param zone Zone
 * @param ssm SSM attribute
 */
function getSsmFillColor(zone: SoilAnalysisZone, ssm: SsmAttribute) {

    const a = zone.getAnalysis()
    let value = a ? a[ssm.property] : undefined

    if (value && (ssm.unit === "%")) {
        value *= 100
    }

    const cls = ssm.getClass(value)

    if (isObject(cls)) {
        return ssmClassColors[cls.ClassId]
    }

}

/**
 * True if all zones are linked to a standard analysis using the Morgan method
 */
function checkMorganForAllZones({ zones }: { zones: SoilAnalysisZone[] }) {
    return zones && zones.every(z => {
        const a = z?.isAllocated && z.isAllocated()
        return (!a) || checkMorganForZone(z)
    })
}

/**
 * True if the zone is linked to a standard analysis using the Morgan method
 */
function checkMorganForZone(zone: SoilAnalysisZone) {
    // 220328 - Usersnap 2533
    // During imports, this may be called on layers where zone  do not implement SoilAnalysisZone
    return zone?.isMorganAnalysis && zone.isMorganAnalysis()
}

/**
 * Returns the fill color to use for a Morgan attribute
 */
function getMorganFillColor(zone: SoilAnalysisZone, attr: MorganAttribute) {
    const sa = zone.getAnalysis()
    return sa ? soilColors.getMorganColor(attr, sa[attr]) : undefined
}
